<template>
  <div class="tw:flex tw:flex-col tw:gap-1.5">
    <TextPlay
      v-if="label"
      :text="label"
      :for="name"
      tag="label"
      size="sm"
      weight="semibold"
    />

    <!-- Shortcut mode: "as" prop renders the component automatically -->
    <component
      :is="resolvedComponent"
      v-if="resolvedComponent"
      :model-value="fieldValue"
      :error="!!errorMessage"
      v-bind="forwardedAttrs"
      @update:model-value="handleUpdate"
      @blur="handleBlur"
    />

    <!-- Scoped slot mode: full control -->
    <slot
      v-else
      :value="fieldValue"
      :on-update="handleUpdate"
      :on-blur="handleBlur"
      :error="!!errorMessage"
      :error-message="errorMessage"
    />

    <transition name="field-error">
      <TextPlay
        v-if="errorMessage"
        :text="errorMessage"
        size="xs"
        color="var(--feedback-error-500)"
        data-testid="form-field-error"
      />
    </transition>

    <TextPlay
      v-if="description && !errorMessage"
      :text="description"
      size="xs"
      color="var(--text-color)"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, useAttrs, type Component } from 'vue'
import { useField } from 'vee-validate'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  name: string
  label?: string
  description?: string
  rules?: string | Record<string, unknown>
  as?: string | Component
  validateOnInput?: boolean
}>(), {
  label: undefined,
  description: undefined,
  rules: undefined,
  as: undefined,
  validateOnInput: true,
})

const attrs = useAttrs()

// --- VeeValidate field binding ---
const { value: fieldValue, errorMessage, handleChange, handleBlur: veeBlur } = useField(
  () => props.name,
  props.rules,
  {
    validateOnValueUpdate: props.validateOnInput,
    label: props.label,
  }
)

function handleUpdate(val: unknown) {
  handleChange(val, props.validateOnInput)
}

function handleBlur() {
  veeBlur()
}

// --- Resolve component ---
const componentMap: Record<string, () => Promise<Component>> = {
  InputPlayV3: () => import('../../atoms/InputPlayV3/InputPlayV3.vue').then(m => m.default),
  TextareaPlayV2: () => import('../../atoms/TextareaPlayV2/TextareaPlayV2.vue').then(m => m.default),
  TextEditorPlay: () => import('../TextEditorPlay/TextEditorPlay.vue').then(m => m.default),
  SelectPlayV2: () => import('../SelectPlayV2/SelectPlayV2.vue').then(m => m.default),
  MultiselectPlayV2: () => import('../MultiselectPlayV2/MultiselectPlayV2.vue').then(m => m.default),
}

const resolvedComponent = computed(() => {
  if (!props.as) return null
  if (typeof props.as !== 'string') return props.as
  // Lazy resolve from map for string-based references
  return componentMap[props.as]
    ? defineAsyncComp(props.as)
    : null
})

// Define async components to avoid importing everything eagerly
import { defineAsyncComponent } from 'vue'

function defineAsyncComp(name: string) {
  const loader = componentMap[name]
  if (!loader) return null
  return defineAsyncComponent(loader)
}

// Forward attrs that don't belong to FormFieldPlay to the child component
const ownProps = new Set(['name', 'label', 'description', 'rules', 'as', 'validateOnInput'])

const forwardedAttrs = computed(() => {
  const result: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(attrs)) {
    if (!ownProps.has(key)) {
      result[key] = val
    }
  }
  return result
})
</script>

<style scoped>
.field-error-enter-active,
.field-error-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.field-error-enter-from,
.field-error-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
